import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer, useImmerReducer } from "use-immer";

import { IgButton, IgChip, IgTypography } from "@intelligems/ig-theme";
import {
  AnalyzeResourceResponseBodyType,
  ExperienceDto,
  ExperienceListDto,
  StatSigViewType,
} from "@intelligems/ig-types";
import { getEndedAtTs, getFirstStartedAtTs } from "@intelligems/ig-utils";
import { ExitToApp } from "@mui/icons-material";
import { Badge, Grid, SvgIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import { UseQueryResult } from "@tanstack/react-query";

import { useSingleExperienceData } from "../../api/analysis/analysis";
import { getInitialStartFilterTime } from "../../api/formatters/dashboard";
import { getExperienceDtoFromFe } from "../../api/formatters/from-dto/experiences/experiences";
import { useGetOrg } from "../../api/organizations/get-organization";
import {
  AnalyticsFilterReducer,
  AnalyticsFilterReducerAction,
} from "../../api/reducers/analyticsFilterReducer";
import { DashboardPageName } from "../../api/types/dashboards";
import FilterButtonSVG from "../../components/DashboardFilters/FilterButtonSVG";
import { FiltersDialog } from "../../components/DashboardFilters/FiltersDialog";
import { StatSigFilter } from "../../components/DashboardFilters/StatSigFilter";
import { EndShippingTestDialog } from "../../components/Dialogs/EndShippingTestDialog";
import { PriceUpdaterDialog } from "../../features/experienceTests/components/dialogs/PriceUpdaterDialog";
import {
  setDashboardFilter,
  updateDashboardFilter,
} from "../../store/slices/dashboardFilters";
import { selectedExperience } from "../../store/slices/experiences";
import { isDialogOpen, setDialog } from "../../store/slices/global";
import { selectedVariations } from "../../store/slices/variations";
import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
  initialDashboardFiltersState,
} from "../../store/types/dashboardFilters";
import { IAppState } from "../../store/types/store";
import theme from "../../theme";
import { dateTimeConverter } from "../../utils/dateConverters";

import { CompareVariations } from "./Utils/CompareVariations";
import { DataExportDialog } from "./Utils/DataExportDialog";
import { ModifyExperience } from "./Utils/ModifyExperience";
import { ResourceListMenu } from "./Utils/ResourceListMenu";
import { TimezoneLastUpdatedInfo } from "./Utils/TimezoneLastUpdatedInfo";
import { AnalyticsViewTypeDialog } from "./AnalyticsViewTypeDialog";
import { EditTestGroupsDialog } from "./EditTestGroupsDialog";

const PREFIX = "Header";

const classes = {
  filterButtons: `${PREFIX}-filterButtons`,
  text: `${PREFIX}-text`,
  flexRow: `${PREFIX}-flexRow`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.filterButtons}`]: {
    borderRadius: 15,
    textTransform: "none",
  },

  [`& .${classes.text}`]: {
    fontSize: "22px",
    fontWeight: 600,
    letterSpacing: ".50px",
  },

  [`& .${classes.flexRow}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));

interface DashboardHeaderProps {
  activeExperiences: ExperienceListDto[] | ExperienceDto[];
  pageName: DashboardPageName;
  latestRunTs: number | undefined;
}

export const DashboardHeader = ({
  activeExperiences,
  pageName,
  latestRunTs,
}: DashboardHeaderProps) => {
  const currExperience = useSelector(selectedExperience);
  const currVariations = useSelector(selectedVariations);
  const organization = useGetOrg();
  const dispatch = useDispatch();
  const customEventData: UseQueryResult<AnalyzeResourceResponseBodyType> =
    useSingleExperienceData(currExperience, "customEventOptions");

  const countryCodesData: UseQueryResult<AnalyzeResourceResponseBodyType> =
    useSingleExperienceData(currExperience, "countryCodeOptions");

  const allCustomEventsList: string[] = useMemo(() => {
    if (customEventData.isSuccess) {
      return customEventData.data.metrics.map((metric) => metric["EVENT_NAME"]);
    } else return [];
  }, [customEventData]);

  const allCountryCodesData: string[] = useMemo(() => {
    if (countryCodesData.isSuccess) {
      return countryCodesData.data.metrics.map(
        (metric) => metric["COUNTRY_CODE"]
      );
    } else return [];
  }, [countryCodesData]);

  const isFiltersOpen = useSelector(isDialogOpen("dashboardFilters"));
  const isEditTestGroupsOpen = useSelector(isDialogOpen("editTestGroups"));
  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const viewedAnalyticsDialog = useSelector(
    (state: IAppState) => state.appSettings.viewedAnalyticsDialog
  );

  const handleOpen = () => {
    dispatch(setDialog({ dialog: "dashboardFilters", open: true }));
  };

  const [activeFilterArray, dispatchActiveFilterArray] = useImmerReducer<
    AnalyticsActiveFilterType[],
    AnalyticsFilterReducerAction
  >(AnalyticsFilterReducer, []);

  // Compare Groups Filter
  const [compareGroupsAnchorEl, setCompareGroupsAnchorEl] =
    useImmer<HTMLButtonElement | null>(null);

  const onFilterChipDelete = (key: keyof IExperienceDashboardFilters) => {
    if (key === "testOrdersOnly") {
      dispatch(
        updateDashboardFilter({
          key,
          value:
            initialDashboardFiltersState[key] === undefined
              ? !dashboardFilters.testOrdersOnly
              : !initialDashboardFiltersState[key],
        })
      );
    } else if (key === "startTime") {
      dispatch(
        updateDashboardFilter({
          key,
          value: getFirstStartedAtTs(currExperience.experienceActions),
        })
      );
    } else {
      dispatch(
        updateDashboardFilter({
          key,
          value:
            key === "isZScoreEnabled"
              ? !initialDashboardFiltersState[key]
              : initialDashboardFiltersState[key],
        })
      );
    }
  };

  const onStatSigSwitch = (statSigView: StatSigViewType) => {
    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        statSigView,
      })
    );
  };

  useEffect(() => {
    dispatchActiveFilterArray({
      type: "updateTestOrdersOnly",
      payload: { dashboardFilters },
    });

    dispatchActiveFilterArray({
      type: "updateIsZScoreEnabled",
      payload: { dashboardFilters },
    });

    dispatchActiveFilterArray({
      type: "updateDeviceType",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateVisitorType",
      payload: { dashboardFilters },
    });

    dispatchActiveFilterArray({
      type: "updateDiscountIncludeType",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateChannelType",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateCustomEvents",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateCountryCodes",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateUserBehavior",
      payload: { dashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateUrlParam",
      payload: { dashboardFilters },
    });

    dispatchActiveFilterArray({
      type: "updateEndExperience",
      payload: {
        dashboardFilters,
        experience: currExperience,
        timezone: organization.data.org.timezone ?? "UTC",
      },
    });
    dispatchActiveFilterArray({
      type: "updateStartExperience",
      payload: {
        dashboardFilters,
        experience: currExperience,
        timezone: organization.data.org.timezone ?? "UTC",
      },
    });

    if (
      currExperience.experienceAnalysis.analyticsViewType === "none" &&
      !viewedAnalyticsDialog
    ) {
      dispatch(setDialog({ dialog: "analyticsViewType", open: true }));
    }
  }, [
    dashboardFilters.startTime,
    dashboardFilters.endTime,
    dashboardFilters.testOrdersOnly,
    dashboardFilters.discountIncludeType,
    dashboardFilters.deviceType,
    dashboardFilters.channels?.length,
    dashboardFilters.visitorType,
    dashboardFilters.customEvents,
    dashboardFilters.countryCodes,
    dashboardFilters.userBehavior,
    dashboardFilters.urlParam,
    dashboardFilters.isZScoreEnabled,
    dashboardFilters.maxZScore,
    currExperience,
  ]);

  return (
    <StyledGrid item container direction={"column"}>
      {currExperience && (
        <PriceUpdaterDialog
          noRedirect={true}
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}

      {currExperience && (
        <EndShippingTestDialog
          currExperience={
            getExperienceDtoFromFe(currExperience) as ExperienceDto
          }
        />
      )}
      <IgTypography className={classes.text}>Analytics Dashboard</IgTypography>

      <Grid
        item
        container
        direction={"column"}
        rowSpacing={1}
        columnSpacing={1}
      >
        <ResourceListMenu
          currentResource={currExperience}
          activeResources={activeExperiences}
          resourceType={"experiences"}
        />
        <Grid item container direction={"row"} justifyContent={"space-between"}>
          <Grid item xs={7.9}>
            <IgTypography sx={{ fontSize: "15px", letterSpacing: ".50px" }}>
              {currExperience.description} from{" "}
              {dateTimeConverter(
                getFirstStartedAtTs(currExperience.experienceActions),
                undefined,
                organization.data.org.timezone || undefined,
                true
              )}{" "}
              -{" "}
              {dateTimeConverter(
                getEndedAtTs(currExperience.experienceActions)?.actionTs,
                undefined,
                organization.data.org.timezone || undefined,
                true
              )}
            </IgTypography>
          </Grid>
          <TimezoneLastUpdatedInfo
            showLastUpdatedTime={currExperience.status === "started"}
            latestRunTs={latestRunTs}
          />
        </Grid>
        <Grid
          container
          item
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Grid item xs={6}>
            {["overview", "profit", "revenue"].includes(pageName) ? (
              <CompareVariations
                variations={currVariations}
                compareVariationsAnchorEl={compareGroupsAnchorEl}
                setCompareVariationsAnchorEl={setCompareGroupsAnchorEl}
              />
            ) : pageName === "statSig" ? (
              <StatSigFilter
                statSigView={dashboardFilters.statSigView ?? "best"}
                onStatSigSwitch={onStatSigSwitch}
              />
            ) : null}
          </Grid>
          <Grid
            xs={6}
            columnSpacing={1}
            item
            container
            direction={"row"}
            justifyContent={"flex-end"}
          >
            {currExperience.status !== "ended" && (
              <ModifyExperience currExperience={currExperience} />
            )}
            <Grid item>
              <IgButton
                variant={"outlined"}
                sx={{
                  color: theme.palette.gray.main,
                  border: `1px solid ${theme.palette.disabled.main}`,
                  backgroundColor: "white",
                }}
                onClick={() => {
                  dispatch(setDialog({ dialog: "dataExport", open: true }));
                }}
                startIcon={<ExitToApp />}
              >
                Export
              </IgButton>
            </Grid>
            <Grid item>
              <Badge color={"primary"} badgeContent={activeFilterArray.length}>
                <IgButton
                  variant={"outlined"}
                  sx={{
                    color: theme.palette.gray.main,
                    border: `1px solid ${
                      activeFilterArray.length
                        ? theme.palette.primary.main
                        : theme.palette.disabled.main
                    }`,
                    backgroundColor: "white",
                  }}
                  startIcon={
                    <SvgIcon
                      viewBox="0 0 512 357"
                      component={FilterButtonSVG}
                    />
                  }
                  onClick={handleOpen}
                >
                  Filters
                </IgButton>
              </Badge>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction={"row"}
          alignItems={"center"}
          columnSpacing={1}
          justifyContent={"flex-end"}
        >
          {activeFilterArray.map((filter) => (
            <Grid item key={filter.key}>
              <IgChip
                onDelete={() => {
                  onFilterChipDelete(
                    filter.key as keyof IExperienceDashboardFilters
                  );
                }}
                variant={"filled"}
                label={filter.label}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <EditTestGroupsDialog editTestGroupsDialog={isEditTestGroupsOpen} />
      {isFiltersOpen && (
        <FiltersDialog
          resourceType={"experiences"}
          openFilters={isFiltersOpen}
          pageName={pageName}
          currentResource={currExperience}
          allCustomEventData={allCustomEventsList}
          allCountryCodesData={allCountryCodesData}
        />
      )}
      <DataExportDialog
        showDataReconciliationExport={pageName === "dataReconciliation"}
      />
      <AnalyticsViewTypeDialog experience={currExperience} />
    </StyledGrid>
  );
};
